import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "./redux/actions";
import {
  Row,
  Col,
  Input,
  Tag,
  Table,
  Button,
  Modal,
  Spin,
  message,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { ReactComponent as UploadEditIcon } from "../../assets/upload-edit.svg";
import axios from "axios";
import { baseURL } from "../../globalUtils/axiosConfig";
import _ from "lodash";
// import { ReactComponent as FilterIcon } from "../../assets/filter.svg";
import AMultiSelect from "../../components/AMultiSelect";
import CustomPagination from "../../components/customPagination/CustomPagination";
import StockRegisterCalender from "../LabsStockManagement/tableSection/stockRegisterCalender";
import AttachmentContainer from "../LabsStockManagement/tableSection/AttachmentContainter";
import DashboardExpandedRow from "./ExpandedRow";
import DashboardSignoffCell from "./DashboardSignoffCell";
import moment from "moment";

const DashboardRegisterTable = () => {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  const [statusCheckedList, setStatusCheckedList] = useState([]);
  const [salesRepCheckedList, setSalesRepCheckedList] = useState([]);
  const [req, setReq] = useState(false);
  const [defaultPageOneReq, setDefaultPageOneReq] = useState(false);
  const [sortedInfo, setSortedInfo] = useState(null);
  const [search, setSearch] = useState({});
  const [page, setPage] = useState(1);
  const [resultPerPage, setResultPerPage] = useState(10);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportRecordId, setExportRecordId] = useState(null);
  const [isExported, setIsExported] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const statusFilterOptions = [
    { value: "Incomplete", label: "Incomplete" },
    { value: "Completed", label: "Completed" },
  ];

  const { user } = useSelector((state) => state.authReducer);
  // Keep the column hidden for every non-Super Admin account.
  // Server-side authorization independently enforces the same policy.
  const isSuperAdmin = String(user?.userType ?? user?.user_type ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "") === "superadmin";
  const dashboardRegisters = useSelector((state) => state.dashboardReducer.dashboardRegisters);
  const fetchingLoading = useSelector((state) => state.dashboardReducer.fetchingLoading);
  const totalRecords = useSelector((state) => state.dashboardReducer.totalRecords);

  const salesRepFilterOptions = useMemo(() => {
    if (!dashboardRegisters || dashboardRegisters.length === 0) {
      return [];
    }
    const uniqueSalesReps = {};
    dashboardRegisters.forEach(register => {
      if (register?.user?.id && register?.user?.firstName) {
        const key = register.user.id;
        if (!uniqueSalesReps[key]) {
          uniqueSalesReps[key] = {
            value: register.user.id,
            label: `${register.user.firstName} ${register.user.lastName || ""}`.trim()
          };
        }
      }
    });
    return Object.values(uniqueSalesReps);
  }, [dashboardRegisters]);

  useEffect(() => {
    const params = {
      ...search,
      page: page || 1,
      perPage: resultPerPage || 10,
    };

    if (sortedInfo?.columnKey && sortedInfo?.order) {
      if (sortedInfo.columnKey !== 'salesRep') {
        const sortColumn = sortedInfo.columnKey === 'statusName' ? 'status' : sortedInfo.columnKey;
        params.sortBy = sortColumn;
        params.sortOrder = sortedInfo.order === 'ascend' ? 'asc' : 'desc';
      }
    }

    dispatch(actions.fetchDashboardRegisters(params));
  }, [req, page, defaultPageOneReq, resultPerPage]);

  useEffect(() => {
    if (dashboardRegisters && dashboardRegisters.length > 0) {
      const formattedData = dashboardRegisters.map((register) => {
        const stockTakeCount = register.stockTakes?.length || 0;
        const omniQty = register.omniQty || 0;
        const stockQty = stockTakeCount;
        const rawVarianceQty = register.varianceQty !== undefined && register.varianceQty !== null ? register.varianceQty : (omniQty - stockQty);
        const varianceQty = Math.abs(rawVarianceQty);

        const salesRepName = register.user
          ? `${register.user.firstName || ""} ${register.user.lastName || ""}`.trim()
          : "";

        const labName = register.labSetup?.labName || register.lab_name || "";
        const region = register.labSetup?.branchCode || register.branch_code || "";

        return {
          id: register.id,
          key: register.id,
          labName: labName,
          region: region,
          labSetup: register.labSetup || {},
          date: register.date ? moment(register.date).format("DD MMM YYYY") : "",
          uniqueId: register.uniqueId || "",
          salesRep: salesRepName,
          omniQty: omniQty,
          stockQty: stockQty,
          varianceQty: varianceQty,
          statusName: register.status || "Incomplete",
          statusColor: register.statusColor || "#fcb9b8",
          attach: register.attachments || [],
          varianceReports: register.varianceReports || register.variance_reports || [],
          dashboardCheckedAt: register.dashboardCheckedAt || null,
          dashboardCheckedByUserId: register.dashboardCheckedByUserId ?? null,
          dashboardCheckedByName: register.dashboardCheckedByName || null,
          rawData: register,
        };
      });

      setData(formattedData);
    } else if (dashboardRegisters && dashboardRegisters.length === 0) {
      setData([]);
    }
  }, [dashboardRegisters]);

  const [debouncedCallApi] = useState(() =>
    _.debounce(() => setReq((prev) => !prev), 1000)
  );

  const [debouncedDefaultPageOneCallApi] = useState(() =>
    _.debounce(() => setDefaultPageOneReq((prev) => !prev), 1000)
  );

  // const onCloseFilter = () => {
  //   setShowFilter(false);
  //   setSearch({});
  //   debouncedDefaultPageOneCallApi();
  // };
  const handleExportRegister = async (recordId) => {
    if (!recordId) return false;

    const record = data.find(r => r.id === recordId);
    const uniqueId = record?.uniqueId || 'unknown';
    const fileName = `dashboard-register-${uniqueId}.xlsx`;

    try {
      const response = await axios.get(
        `/stock-management-register/${recordId}/export`,
        { responseType: "blob" }
      );

      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [{
              description: 'Excel Files',
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] }
            }]
          });
          const writable = await handle.createWritable();
          await writable.write(response.data);
          await writable.close();
          return true;
        } catch (pickerError) {
          if (pickerError.name === 'AbortError') {
            return false;
          }
          throw pickerError;
        }
      } else {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        return true;
      }
    } catch (error) {
      message.error("Failed to export register");
      return false;
    }
  };

  const handleExportClick = (recordId) => {
    if (!recordId) return;
    setExportRecordId(recordId);
    setExportModalVisible(true);
    setIsExported(false);
    setIsExporting(false);
  };

  const handleExportConfirm = async () => {
    if (!exportRecordId || isExporting) return;

    setIsExporting(true);
    const success = await handleExportRegister(exportRecordId);
    setIsExporting(false);

    if (success) {
      setIsExported(true);
    }
  };

  const handleExportCancel = () => {
    setExportModalVisible(false);
    setExportRecordId(null);
    setIsExported(false);
    setIsExporting(false);
  };
  // Apply both sign-off and unsign responses to this row; later Dashboard
  // GET requests remain authoritative for the name and timestamp.
  const handleDashboardSignoffUpdated = useCallback((registerId, saved) => {
    setData((rows) => rows.map((row) => row.id === registerId ? {
      ...row,
      dashboardCheckedAt: saved.dashboardCheckedAt,
      dashboardCheckedByUserId: saved.dashboardCheckedByUserId,
      dashboardCheckedByName: saved.dashboardCheckedByName,
      rawData: { ...row.rawData, ...saved },
    } : row));
  }, []);

  // On a stale/concurrent sign-off state, reload the authoritative Dashboard
  // response instead of guessing whether the row is checked.
  const handleDashboardSignoffConflict = useCallback(() => {
    setReq((value) => !value);
  }, []);

  const handleColOnClick = (record) => {
    if (expandedRowKeys.includes(record.id)) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys([record.id]);
    }
  };

  const handleDateInput = (dates) => {
    setDateRange(dates);
    const dateStrings = dates ? [dates[0]?.format('YYYY-MM-DD'), dates[1]?.format('YYYY-MM-DD')] : ['', ''];
    setSearch({
      ...search,
      startDate: dateStrings[0] || '',
      endDate: dateStrings[1] || '',
    });
    debouncedDefaultPageOneCallApi();
  };

  const getColumnName = (i) => {
    const columnNames = [
      "Lab Name",
      "Region",
      "Date",
      "Unique Id",
      "Sales Rep",
      "Omni Qty",
      "Stock Qty",
      "Variance Qty",
      "Status",
      "Attach",
    ];
    return columnNames[i] || `Column ${i + 1}`;
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const searchInput = (key) => {
    if (key === 'date') {
      return (
        <StockRegisterCalender
          date={dateRange}
          format="DD MMM YY"
          handleDateInput={handleDateInput}
          setDate={setDateRange}
          setDatePickerOpened={setDatePickerOpened}
        />
      );
    }
    return (
      <Input
        placeholder={""}
        onClick={(e) => e.stopPropagation()}
        value={search[key] || ""}
        onChange={(e) => {
          setSearch({
            ...search,
            [key]: e.target.value,
          });
          debouncedDefaultPageOneCallApi();
        }}
      />
    );
  };

  const handleMultiSelect = (list, key) => {
    setSearch({
      ...search,
      [key]: list,
    });
  };

  const handleSelectOnchange = (value, options, key, setHandler) => {
    if (value.includes("all")) {
      setHandler([...options.map((item) => item.value)]);
      value = options.map((item) => item.value);
    } else if (value.includes("not-all")) {
      setHandler([]);
      value = [];
    } else {
      setHandler(value);
    }
    handleMultiSelect(value, key);
    debouncedCallApi();
  };

  const columns = useMemo(() => [
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(0)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.labName || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    labName: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "labName",
      key: "labName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "labName" && sortedInfo?.order,
      width: 150,
      render: (labName, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {labName}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(1)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.region || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    region: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "region",
      key: "region",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "region" && sortedInfo?.order,
      width: 100,
      render: (region, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {region}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="left" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(2)}</div>
            <div>{searchInput("date")}</div>
          </div>
        );
      },
      dataIndex: "date",
      key: "date",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "date" && sortedInfo?.order,
      width: 100,
      render: (date, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {date}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(3)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.uniqueId || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    uniqueId: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "uniqueId",
      key: "uniqueId",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "uniqueId" && sortedInfo?.order,
      width: 100,
      render: (uniqueId, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {uniqueId}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">{getColumnName(4)}</div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="salesRep-selector"
                debouncedCallApi={debouncedCallApi}
                disabled={false}
                placeholder=""
                selectTags={salesRepCheckedList}
                onSelectChange={(value) =>
                  handleSelectOnchange(
                    value,
                    salesRepFilterOptions,
                    "userIds",
                    setSalesRepCheckedList
                  )
                }
                data={salesRepFilterOptions}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "salesRep",
      key: "salesRep",
      sorter: (a, b) => {
        const nameA = (a.salesRep || "").toLowerCase();
        const nameB = (b.salesRep || "").toLowerCase();
        return nameA.localeCompare(nameB);
      },
      sortOrder: sortedInfo?.columnKey === "salesRep" && sortedInfo?.order,
      width: 150,
      render: (salesRep, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {salesRep}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(5)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.omniQty || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    omniQty: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "omniQty",
      key: "omniQty",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "omniQty" && sortedInfo?.order,
      width: 80,
      align: "center",
      render: (omniQty, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {omniQty}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(6)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.stockQty || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    stockQty: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "stockQty",
      key: "stockQty",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "stockQty" && sortedInfo?.order,
      width: 90,
      align: "center",
      render: (stockQty, record) => (
        <div onClick={() => handleColOnClick(record)}>
          {stockQty}
        </div>
      ),
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(7)}</div>
            <div>
              <Input
                placeholder={""}
                onClick={(e) => e.stopPropagation()}
                value={search.varianceQty || ""}
                onChange={(e) => {
                  setSearch({
                    ...search,
                    varianceQty: e.target.value,
                  });
                  debouncedDefaultPageOneCallApi();
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "varianceQty",
      key: "varianceQty",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "varianceQty" && sortedInfo?.order,
      width: 100,
      align: "center",
      render: (varianceQty, record) => {
        const val = record?.varianceQty;
        return (
          <div onClick={() => handleColOnClick(record)}>
            {val !== 0 ? (
              <span
                style={{
                  display: "inline-block",
                  width: "34px",
                  height: "20px",
                  backgroundColor: "#e20f00",
                  color: "white",
                  borderRadius: "3px",
                  textAlign: "center",
                  lineHeight: "18px",
                  fontSize: "11px",
                  fontWeight: 500,
                }}
              >
                {val}
              </span>
            ) : (
              val
            )}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" style={{ width: "100%" }}>
            <div className="special-table-header-text">{getColumnName(8)}</div>
            <div style={{ width: "100%" }}>
              <AMultiSelect
                dropdownClassName="status-selector"
                debouncedCallApi={debouncedCallApi}
                disabled={false}
                placeholder=""
                selectTags={statusCheckedList}
                onSelectChange={(value) =>
                  handleSelectOnchange(
                    value,
                    statusFilterOptions,
                    "status",
                    setStatusCheckedList
                  )
                }
                data={statusFilterOptions}
                filterOption={(input, option) => {
                  return (
                    String(option?.label)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  );
                }}
              />
            </div>
          </div>
        );
      },
      dataIndex: "statusName",
      key: "statusName",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "statusName" && sortedInfo?.order,
      ellipsis: true,
      width: 100,
      align: "center",
      render: (statusName, record) => {
        let tagColor = record.statusColor || "#fcb9b8";

        if (statusName?.toLowerCase() === "completed") {
          tagColor = "#a7e0a5";
        } else if (statusName?.toLowerCase() === "incomplete") {
          tagColor = "#fcb9b8";
        }

        return (
          <Tag
            color={tagColor}
            style={{
              color: "#383838",
              width: "90px",
              height: "20px",
              borderRadius: "10px",
              textAlign: "center",
              fontSize: "12px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {capitalizeFirstLetter(statusName)}
          </Tag>
        );
      },
    },
    {
      title: () => {
        return (
          <div align="top" className="select-parent-div">
            <div className="special-table-header-text">{getColumnName(9)}</div>
            <div>{searchInput("attach")}</div>
          </div>
        );
      },
      dataIndex: "attach",
      key: "attach",
      sorter: true,
      sortOrder: sortedInfo?.columnKey === "attach" && sortedInfo?.order,
      width: 60,
      render: (attach, record) => {
        const isExpanded = expandedRowKeys.includes(record.id);
        return (
          <AttachmentContainer
            recordType="labs_stock_management"
            record={record.rawData}
            color="#ed017f"
            disabled={false}
            isExpanded={isExpanded}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (text, record) => {
        return (
          <Button
            type="text"
            icon={
              <UploadEditIcon
                className="custom-icon"
                style={{ width: '18px', height: '18px', color: '#ed017f', fill: '#ed017f' }}
              />
            }
            onClick={(e) => {
              e.stopPropagation();
              handleExportClick(record.id);
            }}
            style={{
              color: '#ed017f',
              border: 'none',
              background: 'transparent',
              padding: '4px 8px',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            title="Export register"
          />
        );
      },
    },
    // Append to the RIGHT of the existing Export column. No other styling,
    // column widths, or table behavior is changed.
    ...(isSuperAdmin ? [{
      title: () => (
        <div align="top" className="select-parent-div" style={{ textAlign: "center", width: "100%" }}>
          <div className="special-table-header-text" style={{ textAlign: "center" }}>
            CHECKED
          </div>
          {/* Keep the filter-row height so CHECKED aligns with the other labels. */}
          <div aria-hidden="true" style={{ visibility: "hidden" }}>
            <Input disabled tabIndex={-1} />
          </div>
        </div>
      ),
      key: "dashboardSignoff",
      width: 65,
      align: "center",
      render: (_, record) => (
        <DashboardSignoffCell
          registerId={record.id}
          registerUniqueId={record.uniqueId}
          checkedAt={record.dashboardCheckedAt}
          checkedByName={record.dashboardCheckedByName}
          canSignOff={isSuperAdmin}
          onUpdated={handleDashboardSignoffUpdated}
          onConflict={handleDashboardSignoffConflict}
        />
      ),
    }] : []),
  ], [search, dateRange, statusCheckedList, salesRepCheckedList, sortedInfo, salesRepFilterOptions, debouncedCallApi, debouncedDefaultPageOneCallApi, handleSelectOnchange, isSuperAdmin, handleDashboardSignoffUpdated, handleDashboardSignoffConflict]);

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    if (pagination.current && pagination.current !== page) {
      setPage(pagination.current);
    }
    if (pagination.pageSize && pagination.pageSize !== resultPerPage) {
      setResultPerPage(pagination.pageSize);
    }
    debouncedCallApi();
  };

  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const expandedRowRender = useCallback((record) => {
    return <DashboardExpandedRow record={record} />;
  }, []);

  const expandableConfig = useMemo(() => ({
    expandedRowRender,
    expandIcon: null,
    showExpandColumn: false,
    expandedRowKeys: expandedRowKeys,
    rowExpandable: () => true,
  }), [expandedRowRender, expandedRowKeys]);

  const handleRowExpand = (record) => {
    if (expandedRowKeys.includes(record.id)) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys([record.id]);
    }
  };

  return (
    <Row className="doc-control-parent-row">
      <Col span={24} className="heading-col">
        <Row align="middle" gutter={[50, 0]} justify="space-between">
          <Col>
            <Row gutter={[50, 0]} align="middle">
              <Col>
                <div>
                  <span className="heading-span" style={{ color: "#000" }}>
                    DASHBOARD
                  </span>
                </div>
              </Col>
            </Row>
          </Col>
          {/* <Col>
            <FilterIcon
              className="pink-icon"
              onClick={() => setShowFilter(!showFilter)}
              style={{ cursor: 'pointer', width: '24px', height: '24px', color: '#ed017f' }}
            />
          </Col> */}
        </Row>
      </Col>
      <Col span={24}>
        <Table
          scroll={{ x: 'max-content' }}
          className={`special-table ims-doc-table`}
          expandable={expandableConfig}
          onRow={({ id }) =>
            expandedRowKeys.includes(id) && {
              className: `expand-doc-parent`,
            }
          }
          rowKey={(record) => record.id}
          rowClassName="editable-row"
          dataSource={data}
          columns={columns}
          onChange={handleChange}
          loading={fetchingLoading}
          pagination={false}
          onExpand={(expanded, record) => {
            if (expanded) {
              setExpandedRowKeys([record.id]);
            } else {
              setExpandedRowKeys([]);
            }
          }}
        />
        <CustomPagination
          page={page}
          setPage={setPage}
          resultPerPage={resultPerPage}
          setResultPerPage={setResultPerPage}
          totalRecord={totalRecords}
          debouncedCallApi={debouncedCallApi}
        />
      </Col>
      
      <Modal
        title="EXPORT"
        open={exportModalVisible}
        onCancel={handleExportCancel}
        footer={null}
        width={350}
      >
        <div style={{ padding: '20px 0', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '16px', color: '#333', fontFamily: 'poppins, Arial' }}>
            {isExported
              ? "Document Exported Successfully."
              : `Export Dashboard Register ID ${data.find(r => r.id === exportRecordId)?.uniqueId || 'N/A'}`
            }
          </p>
        </div>
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          {isExporting ? (
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: '#ed017f' }} spin />} />
          ) : (
            <Button
              type="primary"
              icon={!isExported ? (
                <UploadEditIcon
                  className="white-icon"
                  style={{ width: '16px', height: '16px', color: '#ffffff', fill: '#ffffff' }}
                />
              ) : undefined}
              className="add-doc-btn"
              onClick={isExported ? handleExportCancel : handleExportConfirm}
              disabled={isExporting}
              style={{
                background: '#ed017f',
                borderColor: '#ed017f',
                borderRadius: '2px',
                color: '#ffffff',
                fontFamily: 'poppins, Arial',
                padding: '8px 24px',
                height: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isExported ? "Close" : "Export File"}
            </Button>
          )}
        </div>
      </Modal>
    </Row>
  );
};

export default DashboardRegisterTable;